// Ricerca e filtri Brand, Serie e Tipologia di Listini e Magazzino [sc-48920].
// Spec: docs/superpowers/specs/2026-09-29-sc-48920-ricerca-articolo-design.md
// Le voci arrivano da GET item/field-values ({ values: [{ value, count }] }).

export const FILTRI_VUOTI = Object.freeze({
  brand: "",
  serie: "",
  tipologia: "",
});

const maiuscole = (t) => t.replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/**
 * Voci di un filtro per StatusSelect: «Tutti…» e i valori in ordine
 * alfabetico. Niente voce «Senza serie/tipologia»: decisione del 2026-09-29,
 * gli articoli senza valore si vedono con «Tutti».
 *
 * @param {Array<{value, count}>} values  risposta di field-values
 * @param {{ tutti: string, capitalizza?: boolean }} testi
 */
export function opzioniFiltro(values, { tutti, capitalizza = false }) {
  const presenti = (values || [])
    .filter((v) => v.value)
    .map((v) => v.value)
    .sort((a, b) => a.localeCompare(b, "it"));
  return [
    { value: "", label: tutti },
    ...presenti.map((v) => ({
      value: v,
      label: capitalizza ? maiuscole(v) : v,
    })),
  ];
}

const normalizza = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * Voci della tendina che contengono il testo cercato, in qualsiasi punto,
 * senza badare a maiuscole e accenti. La voce «Tutti…» (valore vuoto) resta
 * sempre in cima. Spec §5.2.
 */
export function filtraOpzioni(opzioni, testo) {
  const cerca = normalizza(testo);
  if (!cerca) return opzioni || [];
  return (opzioni || []).filter(
    (o) => o.value === "" || normalizza(o.label).includes(cerca)
  );
}

/** Quanti valori veri (non vuoti) ha un filtro: serve a decidere se mostrarlo. */
export function valoriPresenti(values) {
  return (values || []).filter((v) => v.value).length;
}

/**
 * Nuovo stato dei filtri dopo un cambio. Brand e serie sono indipendenti;
 * cambiare brand o serie azzera la tipologia (richiesta del 2026-09-29).
 */
export function cambiaFiltro(filtri, campo, valore) {
  const nuovo = { ...filtri, [campo]: valore ?? "" };
  const cambiato = (filtri?.[campo] ?? "") !== nuovo[campo];
  if (cambiato && (campo === "brand" || campo === "serie"))
    nuovo.tipologia = "";
  return nuovo;
}

/**
 * La serie scelta resta valida con il nuovo brand? Sì se è fra le sue serie
 * (senza badare a maiuscole), altrimenti si azzera: con brand e serie che
 * non stanno insieme l'elenco sarebbe sempre vuoto.
 */
export function serieDisponibile(serie, values) {
  if (!serie) return "";
  const chiave = String(serie).trim().toLowerCase();
  const trovata = (values || []).some(
    (v) => v.value && String(v.value).trim().toLowerCase() === chiave
  );
  return trovata ? serie : "";
}

/** C'è qualcosa da azzerare (un filtro scelto o una ricerca scritta)? */
export function filtriAttivi(filtri, ricerca = "") {
  return Boolean(
    String(ricerca || "").trim() ||
      filtri?.brand ||
      filtri?.serie ||
      filtri?.tipologia
  );
}

/** Parametri per item/get-all: solo quelli valorizzati. */
export function parametriFiltri(filtri) {
  return {
    ...(filtri?.brand ? { filterBrand: filtri.brand } : {}),
    ...(filtri?.serie ? { serie: filtri.serie } : {}),
    ...(filtri?.tipologia ? { tipologia: filtri.tipologia } : {}),
  };
}

/**
 * Seconda riga della voce articolo nel preventivo: codice · brand · serie,
 * solo quelli presenti. Fa capire perché un articolo è uscito dalla ricerca.
 */
export function dettaglioVoceArticolo(item) {
  return [item?.itemCode, item?.brand, item?.serie]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" · ");
}

/**
 * Riga sotto il titolo delle card dei Listini: «10.717 prodotti»,
 * «1 prodotto», «Listino vuoto». Conteggi non ancora arrivati (o in errore):
 * niente riga, per non dire «vuoto» a torto.
 */
export function testoConteggioListino(counts, categoria) {
  if (!counts) return "";
  const n = Number(counts[categoria]) || 0;
  if (!n) return "Listino vuoto";
  // punto delle migliaia sempre: toLocaleString("it-IT") non lo mette sotto
  // le 10.000 (1502, non 1.502)
  const cifre = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return n === 1 ? "1 prodotto" : `${cifre} prodotti`;
}
